import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Autocomplete, TextField, CircularProgress } from '@mui/material';
import debounce from 'lodash/debounce';
import {
  setCurrentPodcast,
  fetchPodcasts,
  setSelectedGenre,
  setBrowseMode,
  setShowBrowseGrid,
  resetBrowseMode,
  resetGenres,
  setSelectedCountry,
  setShouldFetchTopPodcasts,
} from '../../redux/slices/podcastsSlice';
import { trackEvent } from '../../redux/slices/analyze13Slice';
import { useStyles, StyledPopper } from './styles';
import { BrowseChartsFilters } from '../BrowsePodcasts/BrowsePodcastsFilters';
import { FavoritesList } from '../FavoritesList/FavoritesList';
import cx from 'classnames';
import { updatePodcastUrl } from '../../utils/updatePodcastUrl';

const TRACKED_PODS_KEY = 'a13_tracked_pods';
const isTrackedPod = (podcastId) => {
  try {
    const ids = JSON.parse(localStorage.getItem(TRACKED_PODS_KEY) || '[]');
    return ids.includes(String(podcastId));
  } catch { return false; }
};
const markPodTracked = (podcastId) => {
  try {
    const ids = JSON.parse(localStorage.getItem(TRACKED_PODS_KEY) || '[]');
    const set = new Set(ids);
    set.add(String(podcastId));
    localStorage.setItem(TRACKED_PODS_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
};

export const Search = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const {
    podcasts: { data, loading },
    currentPodcast,
    browseMode,
    demoModePodcasts,
    activePlatform,
  } = useSelector((state) => state.podcasts);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const podId = searchParams.get('podId');
    if (podId) {
      dispatch(fetchPodcasts({ name: podId, isId: true }));
    }
  }, []);

  useEffect(() => {
    if (searchValue) {
      dispatch(fetchPodcasts({ name: searchValue }));
    }
  }, [activePlatform]);

  useEffect(() => {
    if (isFirstRender.current && data.length === 1 && !demoModePodcasts.length) {
      const podcast = data[0];
      setSearchValue(podcast.collectionName);
      updatePodcastUrl(podcast.collectionId);
      dispatch(setCurrentPodcast(podcast));
      isFirstRender.current = false;

      // Track auto-load from ?podId= URL param
      const podId = searchParams.get('podId');
      const feedUrl = podcast.feedUrl || podcast.collectionViewUrl;
      if (podId && feedUrl) {
        const pid = String(podcast.collectionId);
        if (!isTrackedPod(pid)) {
          markPodTracked(pid);
          dispatch(trackEvent({
            event: 'podcast_selected',
            rssUrl: feedUrl,
            showTitle: podcast.collectionName || '',
            podcastId: pid,
          }));
        }
      }
    }
  }, [data, dispatch, demoModePodcasts]);

  useEffect(() => {
    if (currentPodcast) {
      setSearchValue(currentPodcast.collectionName);
    }
  }, [currentPodcast]);

  const debouncedFetch = useCallback(
    (value) => {
      if (value.trim()) {
        setIsSearching(true);
        dispatch(fetchPodcasts({ name: value })).finally(() => setIsSearching(false));
      }
    },
    [dispatch, activePlatform]
  );

  const debouncedSearch = useMemo(() => debounce(debouncedFetch, 800), [debouncedFetch]);

  const handleInputChange = (event, value, reason) => {
    if (reason !== 'reset') {
      setSearchValue(value);
    }
    if (reason === 'input') {
      debouncedSearch(value);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && data.length === 1) {
      hideBrowseMode();
      dispatch(setCurrentPodcast(data[0]));
      updatePodcastUrl(data[0].collectionId);
      setOpen(false);
    }
  };

  const hideBrowseMode = () => {
    dispatch(setShowBrowseGrid(false));
    dispatch(resetBrowseMode());
  };

  const handlePodcastSelect = (event, podcast) => {
    dispatch(setShouldFetchTopPodcasts(true));
    if (podcast) {
      hideBrowseMode();
      dispatch(resetGenres());
      dispatch(setSelectedCountry('United States'));
      dispatch(setCurrentPodcast(podcast));
      updatePodcastUrl(podcast.collectionId);
      const feedUrl = podcast.feedUrl || podcast.collectionViewUrl;
      if (feedUrl) {
        const pid = podcast.collectionId ? String(podcast.collectionId) : null;
        if (pid && !isTrackedPod(pid)) {
          markPodTracked(pid);
          dispatch(trackEvent({
            event: 'podcast_selected',
            rssUrl: feedUrl,
            showTitle: podcast.collectionName || '',
            podcastId: pid,
          }));
        }
      }
    }
  };

  const handleClose = () => {
    if (!isSearching) {
      setOpen(false);
      dispatch(setSelectedGenre(null));
    }
  };

  const sortedPodcasts = useMemo(() => {
    const podcastNameCount = new Map();
    const duplicateNames = new Set();

    data.forEach((podcast) => {
      const name = podcast.collectionName.toLowerCase();
      if (podcastNameCount.has(name)) {
        duplicateNames.add(name);
      }
      podcastNameCount.set(name, (podcastNameCount.get(name) || 0) + 1);
    });

    const sortedData = [...data].sort((a, b) => {
      if (a.collectionName.toLowerCase() === searchValue.toLowerCase()) return -1;
      if (b.collectionName.toLowerCase() === searchValue.toLowerCase()) return 1;
      return 0;
    });

    return {
      sortedData,
      hasDuplicates: duplicateNames.size > 0,
      duplicateNames,
    };
  }, [data, searchValue]);

  const getPodcastDisplayName = useCallback(
    (podcast) => {
      if (!podcast) return '';

      if (sortedPodcasts.hasDuplicates) {
        const name = podcast.collectionName.toLowerCase();
        if (sortedPodcasts.duplicateNames.has(name)) {
          return `${podcast.collectionName} (${podcast.artistName})`;
        }
      }
      return podcast.collectionName;
    },
    [sortedPodcasts]
  );

  return (
    <Box className={classes.searchWrapper}>
      <Box className={classes.navigationWrapper}>
        <Box
          className={cx(classes.navLink, { [classes.activeNav]: !browseMode })}
          onClick={() => dispatch(setBrowseMode(false))}
        >
          <Typography variant="h7" component="h2" className={classes.searchTitle}>
            Search podcasts
          </Typography>
        </Box>
        <Box className={classes.divider}>|</Box>
        <Box
          className={cx(classes.navLink, { [classes.activeNav]: browseMode })}
          onClick={() => {
            dispatch(setBrowseMode(true));
            dispatch(setShowBrowseGrid(true));
          }}
        >
          <Typography variant="h7" component="h2" className={classes.searchTitle}>
            Browse charts
          </Typography>
        </Box>
        <Box className={classes.divider}>|</Box>
        <FavoritesList />
      </Box>
      {browseMode ? (
        <BrowseChartsFilters />
      ) : (
        <Autocomplete
          slots={{
            popper: StyledPopper,
          }}
          className={classes.searchAutocomplete}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          onChange={(event, newValue) => {
            handlePodcastSelect(event, newValue);
          }}
          inputValue={searchValue}
          onInputChange={(event, value, reason) => handleInputChange(event, value, reason)}
          onKeyDown={handleKeyDown}
          filterOptions={(x) => x}
          getOptionLabel={(option) => getPodcastDisplayName(option)}
          options={sortedPodcasts.sortedData}
          loading={loading}
          clearOnBlur={false}
          noOptionsText="No podcasts"
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Podcast name"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.collectionId} style={{ gap: '10px' }}>
              {option.artworkUrl30 && <img src={option.artworkUrl30} width={30} alt="" />}
              {getPodcastDisplayName(option)}
            </li>
          )}
        />
      )}
    </Box>
  );
};
